import { opsFetch, type FetchResult } from "@/lib/opsPaperworkClientBase";
import type { OpsOrderIntakeRow, OpsOrderIntakeStatus } from "@/lib/orderIntakeClient";
import type {
  IdeaRoomCorrelationImportStatus,
  IdeaRoomCorrelationStatus,
  IdeaRoomIntakeCorrelation,
} from "@/lib/ideaRoomCorrelationTypes";
export type { IdeaRoomCorrelationStatus } from "@/lib/ideaRoomCorrelationTypes";

const BASE = "/api/ops/intake";

export type InboundCustomerProfileSummary = {
  id: string;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  contact_snapshot: Record<string, unknown>;
  first_activity_at: string;
  latest_activity_at: string;
  thread_count: number;
  intake_count: number;
  pending_count: number;
  identity_conflict: boolean;
  unresolved_location: boolean;
  sources: string[];
  latest_event_types: string[];
  correlation_statuses: IdeaRoomCorrelationStatus[];
};

export type IdeaRoomShedSuiteCorrelation = IdeaRoomIntakeCorrelation & {
  idearoom_event_id: string;
  idearoom_order_thread_id: string | null;
  idearoom_intake_id: string | null;
  customer_order_intake_id: string | null;
  materialized_deal_id: string | null;
  ambiguity_reason: string | null;
  import_status: IdeaRoomCorrelationImportStatus;
  import_attempt_count: number;
  exact_floor_plan: {
    candidate_id: string;
    event_id: string;
    source_field: string;
    url: string;
  } | null;
};

export type InboundLocationMapping = {
  id: string;
  source: string;
  source_location_key: string;
  source_snapshot: Record<string, unknown>;
  location_id: string;
  mapping_status: "suggested" | "confirmed";
  match_method: string;
  confirmed_at: string | null;
};

export type InboundAction = {
  id: string;
  source: string;
  event_type: string;
  order_thread_id: string | null;
  intake_id: string | null;
  processing_status: string;
  error_message: string | null;
  payload: Record<string, unknown>;
  created_at: string;
  low_value: boolean;
};

export type InboundAssetUrlCandidate = {
  id: string;
  order_thread_id: string | null;
  intake_id: string | null;
  asset_kind: string;
  source_field: string;
  url: string;
  fetch_status: "available" | "pending" | "imported" | "error";
  fetch_error: string | null;
  fetch_attempt_count: number;
  target_deal_id: string | null;
  target_attachment_id: string | null;
  imported_at: string | null;
  created_at: string;
};

export type InboundCustomerThread = {
  id: string;
  source: string;
  stable_key: string;
  identity_conflict: boolean;
  identity_conflict_detail: Record<string, unknown>;
  source_location_keys: string[];
  proposed_location_id: string | null;
  latest_event_type: string | null;
  first_activity_at: string;
  latest_activity_at: string;
  intake: OpsOrderIntakeRow | null;
  actions: InboundAction[];
  location_mappings: InboundLocationMapping[];
  asset_url_candidates: InboundAssetUrlCandidate[];
  correlations: IdeaRoomShedSuiteCorrelation[];
};

export type InboundMappedLocation = {
  id: string;
  name: string;
  location_code: string | null;
  address_1: string | null;
  city: string | null;
  state: string | null;
};

export type ListInboundCustomersParams = {
  status?: OpsOrderIntakeStatus | OpsOrderIntakeStatus[];
  search?: string;
  unresolved_identity?: boolean;
  unresolved_location?: boolean;
  source?: string | string[];
  event_type?: string | string[];
  limit?: number;
  offset?: number;
};

export type ListInboundCustomersResponse = {
  ok: true;
  profiles: InboundCustomerProfileSummary[];
  total: number;
  latest_activity_at: string | null;
};

export type InboundCustomerDetailResponse = {
  ok: true;
  profile: Omit<InboundCustomerProfileSummary, "thread_count" | "intake_count" | "pending_count" | "identity_conflict" | "unresolved_location" | "sources" | "latest_event_types">;
  threads: InboundCustomerThread[];
  actions: InboundAction[];
  location_mappings: InboundLocationMapping[];
  locations: InboundMappedLocation[];
  asset_url_candidates: InboundAssetUrlCandidate[];
  correlations: IdeaRoomShedSuiteCorrelation[];
};

function setListParam(
  query: URLSearchParams,
  key: string,
  value: string | string[] | undefined
) {
  if (value) query.set(key, Array.isArray(value) ? value.join(",") : value);
}

export function listInboundCustomers(
  params: ListInboundCustomersParams = {}
): Promise<FetchResult<ListInboundCustomersResponse>> {
  const query = new URLSearchParams({ op: "customer-profiles" });
  setListParam(query, "status", params.status);
  setListParam(query, "source", params.source);
  setListParam(query, "event_type", params.event_type);
  if (params.search?.trim()) query.set("search", params.search.trim());
  if (params.unresolved_identity) query.set("unresolved_identity", "true");
  if (params.unresolved_location) query.set("unresolved_location", "true");
  if (typeof params.limit === "number") query.set("limit", String(params.limit));
  if (typeof params.offset === "number") query.set("offset", String(params.offset));
  return opsFetch<ListInboundCustomersResponse>(`${BASE}?${query.toString()}`, { method: "GET" });
}

export function getInboundCustomerDetail(
  customerProfileId: string
): Promise<FetchResult<InboundCustomerDetailResponse>> {
  const query = new URLSearchParams({
    op: "customer-detail",
    customer_profile_id: customerProfileId,
  });
  return opsFetch<InboundCustomerDetailResponse>(`${BASE}?${query.toString()}`, { method: "GET" });
}

export function confirmInboundSourceLocation(payload: {
  order_thread_id: string;
  location_id: string;
}): Promise<FetchResult<{ ok: true; order_thread_id: string; location_id: string; backfilled: number }>> {
  return opsFetch(`${BASE}?op=confirm-source-location`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function saveInboundIdeaRoomAsset(
  candidateId: string,
  retry = false
): Promise<FetchResult<{ ok: true; candidate: InboundAssetUrlCandidate }>> {
  return opsFetch(`${BASE}?op=${retry ? "idearoom-asset-retry" : "idearoom-asset-save"}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ candidate_id: candidateId }),
  });
}

export function openSavedInboundIdeaRoomAsset(
  candidateId: string
): Promise<FetchResult<{ ok: true; signed_url: string; signed_expires_at: string }>> {
  return opsFetch(`${BASE}?op=idearoom-asset-open`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ candidate_id: candidateId }),
  });
}
